import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import type { GridSelection } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import type { SelectionModifiers } from 'modules/shift_management/hooks/gridTable/selection/useHeaderDragSelection'

interface UseEventSelectionParams {
  selection: GridSelection
  checkSelectable: (entityId: string) => boolean
  setSelection: (nextSelection: GridSelection | ((prev: GridSelection) => GridSelection)) => void
}

function useEventSelection({ selection, checkSelectable, setSelection }: UseEventSelectionParams): {
  selectedEventKeys: Set<string>
  handleEventSelected: (entityId: string, eventId: string, modifiers: SelectionModifiers) => void
} {
  const toEventKey = useUnsafeCallback((entityId: string, eventId: string) => {
    return `${entityId}:${eventId}`
  }, [])

  const selectedEventKeys = useUnsafeMemo(() => {
    return selection.kind === 'event' ? new Set(selection.keys) : new Set<string>()
  }, [selection])

  const handleEventSelected = useUnsafeCallback(
    (entityId: string, eventId: string, modifiers: SelectionModifiers) => {
      if (!checkSelectable(entityId)) return

      const key = toEventKey(entityId, eventId)
      const isMetaPressed = modifiers.metaKey || modifiers.ctrlKey

      if (isMetaPressed) {
        setSelection((prev) => {
          const next = prev.kind === 'event' ? new Set(prev.keys) : new Set<string>()

          if (next.has(key)) {
            next.delete(key)
          } else {
            next.add(key)
          }

          if (next.size === 0) {
            return { kind: 'none' }
          }

          return { kind: 'event', keys: next }
        })
        return
      }

      setSelection((prev) => {
        if (prev.kind === 'event' && prev.keys.size === 1 && prev.keys.has(key)) {
          return { kind: 'none' }
        }

        return { kind: 'event', keys: new Set([key]) }
      })
    },
    [checkSelectable, setSelection, toEventKey]
  )

  return {
    selectedEventKeys,
    handleEventSelected,
  }
}

export { useEventSelection }
